import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { VexConfigService } from '../../config/vex-config.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AsyncPipe, Location, NgClass } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BreadcrumbsComponent } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { BreadcrumbService } from '@core/services/navigation/breadcrumb.service';
import { SecondaryToolbarPresenceService } from '@core/services/navigation/secondary-toolbar-presence.service';
import { ToolbarDensityService } from '@core/services/navigation/toolbar-density.service';

@Component({
    selector: 'vex-secondary-toolbar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './vex-secondary-toolbar.component.html',
    styleUrls: ['./vex-secondary-toolbar.component.scss'],
    imports: [NgClass, AsyncPipe, BreadcrumbsComponent, MatButtonModule, MatIconModule]
})
export class VexSecondaryToolbarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() current?: string;

  /**
   * One line of context under the title — what this page is for, or which
   * scope it shows. Ignored in compact density: the whole point of a single
   * row is that there is no second one.
   */
  @Input() subtitle?: string;

  /** Optional leading icon, rendered in the standard primary circle.
   *  Ligature name ('home_work') or svgIcon ('mat:home_work'). Mirrors
   *  `PageHeaderComponent.icon` — kept here so a page migrating off
   *  `app-page-header` doesn't lose it. */
  @Input() icon?: string | null;

  /** Show a back arrow before the title. Mirrors `PageHeaderComponent.showBack`. */
  @Input() showBack = false;
  /** Explicit back target; when unset, falls back to browser history. */
  @Input() backLink?: string | null;
  /** Translated aria-label for the back button. */
  @Input() backLabel = 'Back';

  private readonly breadcrumbs = inject(BreadcrumbService);
  private readonly translate = inject(TranslateService);
  private readonly density = inject(ToolbarDensityService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);

  /** True on routes declaring `data: { toolbarDensity: 'compact' }`. */
  readonly compact$ = this.density.compact$;

  /**
   * The title shown on the top row. Uses the explicit `current` when a page
   * provides one; otherwise derives the current page's name from the last
   * breadcrumb crumb so the bar always has a title (avoids a vertical layout
   * jump between pages that set a title and those that don't).
   */
  get displayTitle(): string {
    if (this.current) {
      return this.current;
    }
    const crumbs = this.breadcrumbs.crumbs();
    const last = crumbs[crumbs.length - 1];
    if (!last) {
      return '';
    }
    if (last.dynamic) {
      return this.breadcrumbs.getDynamicLabel(last.paramValue)
        || this.translate.instant('breadcrumbs.loading');
    }
    return last.key ? this.translate.instant(last.key) : '';
  }

  /**
   * True when the last crumb says exactly what the title already says, so the
   * trail should stop one link short instead of rendering "Contracts ›
   * Contracts".
   *
   * The `!dynamic` guard is not optional. A dynamic crumb's label resolves
   * asynchronously (`BreadcrumbService.setDynamicLabel`), so on a detail page
   * it is the loading placeholder for a beat and the resolved name after: a
   * bare comparison would drop the link, then bring it back, and the trail
   * would visibly flicker on every detail route. Static keys resolve
   * synchronously and have no such window.
   */
  get dropLastCrumb(): boolean {
    const crumbs = this.breadcrumbs.crumbs();
    const last = crumbs[crumbs.length - 1];
    if (!last || last.dynamic || !last.key) {
      return false;
    }
    return this.translate.instant(last.key) === this.displayTitle;
  }

  /**
   * When true this instance is the layout-level breadcrumbs-only fallback bar,
   * NOT a real page toolbar — so it must not register presence (that would make
   * the layout believe a page toolbar exists and hide the fallback itself,
   * which would flip-flop endlessly).
   */
  @Input() fallback = false;

  private registered = false;
  private resizeObserver?: ResizeObserver;
  /** The CSS-computed height budget before any override — captured once so a
   *  later shrink (window widened, content changed) knows what to revert to. */
  private baselineHeightPx = 0;
  private heightOverridden = false;

  fixed$ = this.configService.config$.pipe(
    map((config) => config.toolbar.fixed)
  );

  isVerticalLayout$: Observable<boolean> = this.configService
    .select((config) => config.layout)
    .pipe(map((layout) => layout === 'vertical'));

  constructor(
    private readonly configService: VexConfigService,
    private readonly presence: SecondaryToolbarPresenceService
  ) {}

  ngOnInit(): void {
    // `@Input()`s are resolved by ngOnInit — the fallback flag is reliable here.
    if (!this.fallback) {
      this.presence.register();
      this.registered = true;
    }
  }

  /**
   * The bar is `position: fixed` at a height driven by the CSS custom
   * property `--vex-secondary-toolbar-height` — the rest of the layout
   * reserves the same amount of space below it via that same variable. A
   * page whose title/actions no longer fit on one row (`flex-wrap` in the
   * template) grows past that budget; without this, the wrapped row would
   * just overlap the content underneath instead of pushing it down. Only
   * grows past the CSS-computed default, never shrinks below it — the
   * compact/mobile breakpoints already defined in SCSS stay in charge there.
   */
  ngAfterViewInit(): void {
    if (this.fallback || typeof ResizeObserver === 'undefined') {
      return;
    }
    const bar = this.elementRef.nativeElement.querySelector('.secondary-toolbar') as HTMLElement | null;
    if (!bar) {
      return;
    }
    this.baselineHeightPx =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--vex-secondary-toolbar-height')) ||
      bar.clientHeight;
    // ResizeObserver fires outside Angular's zone; the callback only touches
    // a plain DOM style property, not component state, so no change
    // detection is needed — `zone.runOutsideAngular` just keeps it that way.
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.syncHeight(bar));
      this.resizeObserver.observe(bar);
    });
  }

  private syncHeight(bar: HTMLElement): void {
    const needed = bar.scrollHeight;
    const body = document.body;
    if (needed > this.baselineHeightPx) {
      body.style.setProperty('--vex-secondary-toolbar-height', `${needed}px`);
      this.heightOverridden = true;
    } else if (this.heightOverridden) {
      body.style.removeProperty('--vex-secondary-toolbar-height');
      this.heightOverridden = false;
    }
  }

  goBack(): void {
    const target = this.backLink;
    if (target) {
      void this.router.navigate([target]);
    } else if (window.history.length > 1) {
      this.location.back();
    } else {
      void this.router.navigate(['/']);
    }
  }

  ngOnDestroy(): void {
    if (this.registered) {
      this.presence.unregister();
    }
    this.resizeObserver?.disconnect();
    if (this.heightOverridden) {
      document.body.style.removeProperty('--vex-secondary-toolbar-height');
    }
  }
}
